import { useCallback, useState, useRef, useEffect } from 'react';

export const makeId = (prefix = 'id') =>
  `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

function buildSkillsModule(id, type) {
  return {
    id, type,
    skills: [
      { id: makeId('skill'), name: '', desc: '' },
      { id: makeId('skill'), name: '', desc: '' },
    ],
  };
}

function buildQuestionsModule(id, type) {
  return { id, type, questions: [{ id: makeId('q'), q: '', ideal: '', time: 2 }] };
}

function buildExerciseModule(id, type) {
  return { id, type, exerciseType: 'custom', content: '', answerMode: 'text', duration: 30 };
}

const BUILDERS = {
  'skill-conversations': buildSkillsModule,
  'custom-questions': buildQuestionsModule,
  'exercise': buildExerciseModule,
  'resume-conversation': (id, type) => ({ id, type, duration: 15 }),
  'language-assessment': (id, type) => ({ id, type, language: 'en-us', details: '', duration: 10 }),
};

function buildModule(type) {
  const id = makeId(type);
  const builder = BUILDERS[type];
  return builder ? builder(id, type) : { id, type };
}

/**
 * Manages the mutable interview flow: a list of module instances.
 * All setters returned from this hook are stable across renders so they
 * can be passed safely into effects and memoized components.
 */
export default function useInterviewFlow() {
  const [flow, setFlow] = useState([{ id: 'introduction', type: 'introduction' }]);
  const [selectedId, setSelectedId] = useState('introduction');

  // Keep latest flow in a ref so addModule stays stable (no deps → no extra
  // re-renders of children that receive it as a prop).
  const flowRef = useRef(flow);
  useEffect(() => { flowRef.current = flow; }, [flow]);

  const addModule = useCallback((type, { onAfterAdd } = {}) => {
    if (type !== 'exercise') {
      const existing = flowRef.current.find((m) => m.type === type);
      if (existing) {
        setSelectedId(existing.id);
        onAfterAdd?.(existing);
        return;
      }
    }
    const newMod = buildModule(type);
    setFlow((prev) => [...prev, newMod]);
    setSelectedId(newMod.id);
    onAfterAdd?.(newMod);
  }, []);

  const removeModule = useCallback((id) => {
    setFlow((prev) => prev.filter((m) => m.id !== id));
    setSelectedId((prev) => (prev === id ? 'introduction' : prev));
  }, []);

  const updateModule = useCallback((id, patch) => {
    setFlow((prev) => prev.map((m) => (m.id === id ? { ...m, ...patch } : m)));
  }, []);

  const selected = flow.find((m) => m.id === selectedId) || flow[0];

  return {
    flow, setFlow,
    selectedId, setSelectedId,
    selected,
    addModule, removeModule, updateModule,
  };
}
